// public-frontend/src/utils/humanPass.ts
/**
 * humanPass — browser side of the home-page "I am human" gate
 * ─────────────────────────────────────────────────────────────
 * The backend trades one solved Turnstile token for a signed pass
 * (backend/src/services/HumanPass.ts, POST /api/visitors/verify-human).
 * This module keeps that pass in localStorage so a human sees the checkbox once
 * per HUMAN_PASS_TTL_HOURS (default 7 days), not on every tab.
 *
 * The browser only reads the EXPIRY out of the pass, to decide whether to show
 * the gate. It cannot judge the signature and does not need to: the visit
 * beacon sends the pass back and the server decides what it is worth.
 *
 * Storage failures (private mode, blocked site data) degrade to an in-memory
 * pass for the life of the page — the visitor solves the checkbox once per
 * load instead of being locked out.
 *
 * Twin of frontend/src/utils/humanPass.ts — keep the two in step; they differ
 * only in the `app` tag. The sites are different origins, so a pass earned on
 * one does not carry to the other (each asks once per TTL).
 */

// Same CORS-safe base as visitorTracker.ts — never a localhost fallback.
const API_BASE = import.meta.env.VITE_API_URL ?? '';
const STORAGE_KEY = 'ssp_human_pass';

/**
 * Kill switch, baked at build time: VITE_HUMAN_GATE=off disables the gate
 * without a code change (a rebuild is still needed, as for any VITE_ var).
 */
export const HUMAN_GATE_ENABLED =
  String(import.meta.env.VITE_HUMAN_GATE ?? 'on').toLowerCase() !== 'off';

let memoryPass: string | null = null;

function expiryOf(pass: string): number {
  const parts = pass.split('.');
  if (parts.length !== 4 || parts[0] !== 'hp1') return 0;
  const exp = Number(parts[1]);
  return Number.isFinite(exp) ? exp : 0;
}

/** The stored pass if it has not expired, else null (and the stale one is dropped). */
export function getHumanPass(): string | null {
  let pass = memoryPass;
  try {
    pass = localStorage.getItem(STORAGE_KEY) ?? memoryPass;
  } catch {
    /* storage blocked — fall back to memory */
  }
  if (!pass) return null;
  if (expiryOf(pass) <= Date.now()) {
    clearHumanPass();
    return null;
  }
  return pass;
}

export function setHumanPass(pass: string): void {
  memoryPass = pass;
  try {
    localStorage.setItem(STORAGE_KEY, pass);
  } catch {
    /* memory copy is enough for this page */
  }
}

export function clearHumanPass(): void {
  memoryPass = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Unlocks the gate for this page without a pass — used when verification
 * succeeded but the server could not sign one (no secret configured).
 */
let unlockedThisPage = false;
export function markUnlockedForPage(): void {
  unlockedThisPage = true;
}

/** True when the home page may render without showing the gate. */
export function isHumanVerified(): boolean {
  return !HUMAN_GATE_ENABLED || unlockedThisPage || getHumanPass() !== null;
}

export type VerifyResult =
  | { ok: true }
  | { ok: false; reason: 'rejected' | 'rate-limited' | 'network' };

/** Trade a single-use Turnstile token for a pass. Stores it on success. */
export async function verifyHuman(
  token: string,
  context: { sessionId: string; path: string },
): Promise<VerifyResult> {
  try {
    const res = await fetch(`${API_BASE}/api/visitors/verify-human`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        app: 'public-frontend',
        token,
        sessionId: context.sessionId,
        path: context.path,
        screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
        language: navigator.language || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      }),
    });
    if (res.status === 429) return { ok: false, reason: 'rate-limited' };
    if (!res.ok) return { ok: false, reason: 'rejected' };
    const data = (await res.json()) as { ok?: boolean; pass?: string | null };
    if (!data.ok) return { ok: false, reason: 'rejected' };
    if (data.pass) setHumanPass(data.pass);
    else markUnlockedForPage();
    return { ok: true };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * Does this browser CLAIM to be a search crawler? Only a trigger: the server
 * decides (GET /api/visitors/gate-status, reverse+forward DNS). Ordinary
 * visitors never make that request, so the gate costs them nothing extra.
 * Keep in step with ENGINES in backend/src/utils/searchCrawlerVerifier.ts.
 */
export function claimsSearchCrawler(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /googlebot|google-inspectiontool|storebot-google|googleother|bingbot|adidxbot|bingpreview/i.test(
    navigator.userAgent || '',
  );
}

/** How long to wait for the crawler check before falling back to the gate. */
const CRAWLER_CHECK_TIMEOUT_MS = 5_000;

/** True only when the server verified this request as a real search crawler. */
export async function checkCrawlerBypass(): Promise<boolean> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctrl?.abort(), CRAWLER_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/visitors/gate-status`, {
      credentials: 'omit',
      signal: ctrl?.signal,
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { bypass?: boolean };
    return data.bypass === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
